import * as React from 'react';
import { useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from 'primereact/button';
import { FileUpload, FileUploadSelectEvent } from 'primereact/fileupload';
import { InputText } from 'primereact/inputtext';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Dialog } from 'primereact/dialog';
import { Checkbox } from 'primereact/checkbox';
import { ProgressSpinner } from 'primereact/progressspinner';
import { Tag } from 'primereact/tag';
import { Message } from 'primereact/message';
import { Dropdown } from 'primereact/dropdown';
import { Card } from 'primereact/card';
import { Divider } from 'primereact/divider';
import { ConfirmDialog } from 'primereact/confirmdialog';
import { TabView, TabPanel } from 'primereact/tabview';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import CodeCopyBtn from '@view/components/codeCopyBtn/CodeCopyBtn';
import SyntaxHighlighterCode from '@view/components/chatMessage/syntaxHighlighterCode/SyntaxHighlighterCode';
import { OcrConversionInterface } from '@app/interfaces/OcrConversionInterface';
import './OcrScreen.scss';

export interface OcrFormState {
    selectedFile: File | null;
    sourceUrl: string;
    setSourceUrl: (url: string) => void;
    sourceType: 'pdf' | 'image';
    setSourceType: (type: 'pdf' | 'image') => void;
    includeImages: boolean;
    setIncludeImages: (include: boolean) => void;
    processing: boolean;
    activeTab: number;
    setActiveTab: (tab: number) => void;
}

export interface OcrConversionsListState {
    loading: boolean;
    conversions: OcrConversionInterface[];
    page: number;
    setPage: (page: number) => void;
    totalRecords: number;
}

export interface OcrPreviewState {
    visible: boolean;
    content: string;
    conversion: OcrConversionInterface | null;
    tabIndex: number;
    setTabIndex: (index: number) => void;
}

export interface OcrScreenProps {
    // Grouped state
    form: OcrFormState;
    conversionsList: OcrConversionsListState;
    preview: OcrPreviewState;

    // Handler props
    onFileSelect: (event: FileUploadSelectEvent) => void;
    onProcess: (fileUploadRef: React.RefObject<FileUpload>) => void;
    onPreview: (conversion: OcrConversionInterface) => void;
    onDownload: (conversion: OcrConversionInterface) => void;
    onCopyToClipboard: () => void;
    onCopyMarkdownToClipboard: (conversion: OcrConversionInterface) => void;
    onDelete: (conversion: OcrConversionInterface) => void;
    onLoadConversions: () => void;
    onPreviewDialogHide: () => void;
}

const OcrScreen: React.FunctionComponent<OcrScreenProps> = ({
    form,
    conversionsList,
    preview,
    onFileSelect,
    onProcess,
    onPreview,
    onDownload,
    onCopyToClipboard,
    onCopyMarkdownToClipboard,
    onDelete,
    onLoadConversions,
    onPreviewDialogHide
}) => {
    const { t } = useTranslation();
    const fileUploadRef = useRef<FileUpload>(null);

    const statusBodyTemplate = (rowData: OcrConversionInterface) => {
        const statusMap = {
            pending: { severity: 'info' as const, label: t('ocr_screen.status_pending') },
            processing: { severity: 'warning' as const, label: t('ocr_screen.status_processing') },
            completed: { severity: 'success' as const, label: t('ocr_screen.status_completed') },
            failed: { severity: 'danger' as const, label: t('ocr_screen.status_failed') }
        };

        const status = statusMap[rowData.status];
        return <Tag value={status.label} severity={status.severity} />;
    };

    const actionsBodyTemplate = (rowData: OcrConversionInterface) => {
        return (
            <div className="flex gap-2">
                {rowData.status === 'completed' && (
                    <>
                        <Button
                            icon="pi pi-eye"
                            className="p-button-sm p-button-info"
                            onClick={() => onPreview(rowData)}
                            tooltip={t('ocr_screen.preview_tooltip')}
                            data-testid="preview-button"
                        />
                        <Button
                            icon="pi pi-copy"
                            className="p-button-sm p-button-secondary"
                            onClick={() => onCopyMarkdownToClipboard(rowData)}
                            tooltip={t('ocr_screen.copy_markdown_tooltip')}
                            data-testid="copy-button"
                        />
                        <Button
                            icon="pi pi-download"
                            className="p-button-sm p-button-success"
                            onClick={() => onDownload(rowData)}
                            tooltip={t('ocr_screen.download_tooltip')}
                            data-testid="download-button"
                        />
                    </>
                )}
                <Button
                    icon="pi pi-trash"
                    className="p-button-sm p-button-danger"
                    onClick={() => onDelete(rowData)}
                    tooltip={t('ocr_screen.delete_tooltip')}
                    data-testid="delete-button"
                />
            </div>
        );
    };

    const dateBodyTemplate = (rowData: OcrConversionInterface) => {
        return new Date(rowData.createdAt).toLocaleString();
    };

    const sourceTypeOptions = [
        { label: 'PDF', value: 'pdf' },
        { label: 'Image', value: 'image' }
    ];

    return (
        <div className="OcrScreen p-4">
            <ConfirmDialog />

            {/* Upload Section */}
            <Card className="mb-4" title={t('ocr_screen.header')}>
                <TabView activeIndex={form.activeTab} onTabChange={(e) => form.setActiveTab(e.index)}>
                    <TabPanel header={t('ocr_screen.file_upload_tab')}>
                        <div className="flex flex-column">
                            <div className="field">
                                <label>{t('ocr_screen.source_type')}</label>
                                <Dropdown
                                    value={form.sourceType}
                                    options={sourceTypeOptions}
                                    onChange={(e) => form.setSourceType(e.value)}
                                    className="w-full"
                                />
                            </div>

                            <div className="field">
                                <FileUpload
                                    ref={fileUploadRef}
                                    name="file"
                                    accept={form.sourceType === 'pdf' ? '.pdf' : 'image/*'}
                                    maxFileSize={50000000}
                                    onSelect={onFileSelect}
                                    emptyTemplate={
                                        <div className="flex align-items-center flex-column">
                                            <i className="pi pi-cloud-upload" style={{ fontSize: '3em', color: 'var(--primary-color)' }}></i>
                                            <p className="mt-3 mb-0">{t('ocr_screen.drag_drop_file')}</p>
                                            <p className="text-sm text-color-secondary">{t('ocr_screen.or_click_to_select')}</p>
                                        </div>
                                    }
                                    headerTemplate={(options) => {
                                        const { chooseButton } = options;
                                        return (
                                            <div className="flex flex-wrap justify-content-start align-items-center flex-1">
                                                {chooseButton}
                                            </div>
                                        );
                                    }}
                                    chooseOptions={{
                                        className: 'p-button-outlined',
                                        label: t('ocr_screen.choose_file'),
                                        icon: 'pi pi-folder-open'
                                    }}
                                    className="w-full"
                                />
                                {form.selectedFile && (
                                    <Message
                                        severity="success"
                                        text={`${t('ocr_screen.selected_file')}: ${form.selectedFile.name} (${(form.selectedFile.size / 1024 / 1024).toFixed(2)} MB)`}
                                        className="mt-2"
                                    />
                                )}
                            </div>
                        </div>
                    </TabPanel>

                    <TabPanel header={t('ocr_screen.url_input_tab')}>
                        <div className="flex flex-column">
                            <div className="field">
                                <label>{t('ocr_screen.source_type')}</label>
                                <Dropdown
                                    value={form.sourceType}
                                    options={sourceTypeOptions}
                                    onChange={(e) => form.setSourceType(e.value)}
                                    className="w-full"
                                />
                            </div>

                            <div className="field">
                                <label>{t('ocr_screen.source_url')}</label>
                                <InputText
                                    value={form.sourceUrl}
                                    onChange={(e) => form.setSourceUrl(e.target.value)}
                                    placeholder={t('ocr_screen.source_url_placeholder')}
                                    className="w-full"
                                />
                            </div>
                        </div>
                    </TabPanel>
                </TabView>

                <Divider />

                <div className="flex flex-column gap-3">
                    <div className="flex align-items-center">
                        <Checkbox
                            inputId="includeImages"
                            checked={form.includeImages}
                            onChange={(e) => form.setIncludeImages(e.checked || false)}
                        />
                        <label htmlFor="includeImages" className="ml-2">
                            {t('ocr_screen.include_images')}
                        </label>
                    </div>

                    <Button
                        label={t('ocr_screen.process_button')}
                        icon="pi pi-send"
                        onClick={() => onProcess(fileUploadRef)}
                        loading={form.processing}
                        data-testid="process-button"
                        className="p-button-primary"
                    />
                </div>
            </Card>

            {/* Conversions List */}
            <Card header={<div className="flex justify-content-between align-items-center m-3">
                    <h2 className="text-xl font-semibold">{t('ocr_screen.conversions_list')}</h2>
                    <Button
                        icon="pi pi-refresh"
                        className="p-button-sm"
                        onClick={onLoadConversions}
                        loading={conversionsList.loading}
                        tooltip={t('ocr_screen.refresh_tooltip')}
                    />
                </div>}>

                {conversionsList.loading ? (
                    <div className="flex justify-content-center p-4">
                        <ProgressSpinner />
                    </div>
                ) : (
                    <DataTable
                        value={conversionsList.conversions}
                        paginator
                        rows={10}
                        totalRecords={conversionsList.totalRecords}
                        lazy
                        first={(conversionsList.page - 1) * 10}
                        onPage={(e) => conversionsList.setPage(Math.floor(e.first / 10) + 1)}
                        emptyMessage={t('ocr_screen.no_conversions')}
                        responsiveLayout="scroll"
                    >
                        <Column field="id" header={t('ocr_screen.column_id')} style={{ width: '5%' }} />
                        <Column field="sourceType" header={t('ocr_screen.column_source_type')} style={{ width: '10%' }} />
                        <Column field="modelId" header={t('ocr_screen.column_model')} style={{ width: '15%' }} />
                        <Column field="pagesProcessed" header={t('ocr_screen.column_pages')} style={{ width: '10%' }} />
                        <Column field="status" header={t('ocr_screen.column_status')} body={statusBodyTemplate} style={{ width: '12%' }} />
                        <Column field="createdAt" header={t('ocr_screen.column_date')} body={dateBodyTemplate} style={{ width: '18%' }} />
                        <Column body={actionsBodyTemplate} header={t('ocr_screen.column_actions')} style={{ width: '15%' }} />
                    </DataTable>
                )}
            </Card>

            {/* Preview Dialog */}
            <Dialog
                header={
                    <div className="flex justify-content-between align-items-center w-full">
                        <span>{`${t('ocr_screen.preview_header')} - ${preview.conversion?.id}`}</span>
                        <Button
                            icon="pi pi-copy"
                            label={t('ocr_screen.copy_to_clipboard')}
                            className="p-button-sm p-button-text"
                            onClick={onCopyToClipboard}
                            tooltip={t('ocr_screen.copy_to_clipboard')}
                            tooltipOptions={{ position: 'bottom' }}
                        />
                    </div>
                }
                visible={preview.visible}
                style={{ width: '80vw' }}
                onHide={onPreviewDialogHide}
                maximizable
                draggable={false}
                className='OcrPreviewDialog'
            >
                <TabView activeIndex={preview.tabIndex} onTabChange={(e) => preview.setTabIndex(e.index)}>
                    <TabPanel header={t('ocr_screen.preview_tab_rendered')}>
                        <div className="markdown-preview-rendered markdown-body">
                            <ReactMarkdown
                                remarkPlugins={[remarkGfm]}
                                components={{
                                    pre: ({ children }) => (
                                        <pre>
                                            <div className="print-hidden">
                                                <CodeCopyBtn>{children}</CodeCopyBtn>
                                            </div>
                                            {children}
                                        </pre>
                                    ),
                                    code: SyntaxHighlighterCode
                                }}
                            >
                                {preview.content}
                            </ReactMarkdown>
                        </div>
                    </TabPanel>

                    <TabPanel header={t('ocr_screen.preview_tab_code')}>
                        <pre className="markdown-preview-code">{preview.content}</pre>
                    </TabPanel>
                </TabView>
            </Dialog>
        </div>
    );
};

export default OcrScreen;
